/**
 * noo.ma Pro account request — validates the form and posts it to the App Proxy (`/apps/pro/signup` → Notion).
 * Messages are server-rendered and only toggled here, so the Weglot proxy has already translated them.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class DevProSignup extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {boolean} */
  #sending = false;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const form = this.#form;
    if (!form) return;

    form.addEventListener('submit', (event) => this.#onSubmit(event), { signal });
    form.addEventListener('input', (event) => this.#clearFieldError(event.target), { signal });
    form.addEventListener('change', (event) => this.#clearFieldError(event.target), { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  get #form() {
    const form = this.querySelector('[data-ref="form"]');
    return form instanceof HTMLFormElement ? form : null;
  }

  /**
   * @param {string} ref
   */
  #ref(ref) {
    const element = this.querySelector(`[data-ref="${ref}"]`);
    return element instanceof HTMLElement ? element : null;
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {string[]}
   */
  #invalidFields(form) {
    const data = new FormData(form);
    const invalid = [];
    if (!String(data.get('full_name') ?? '').trim()) invalid.push('full_name');
    if (!EMAIL_PATTERN.test(String(data.get('email') ?? '').trim())) invalid.push('email');
    if (!String(data.get('company_name') ?? '').trim()) invalid.push('company_name');
    if (data.get('agreement') !== 'on') invalid.push('agreement');
    return invalid;
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string[]} names
   */
  #showFieldErrors(form, names) {
    let first = null;

    for (const name of names) {
      const control = form.elements.namedItem(name);
      const message = this.querySelector(`[data-error-for="${name}"]`);
      if (!(control instanceof HTMLInputElement) || !(message instanceof HTMLElement)) continue;

      control.setAttribute('aria-invalid', 'true');
      control.setAttribute('aria-describedby', message.id);
      message.hidden = false;
      first ??= control;
    }

    first?.focus();
  }

  /**
   * @param {EventTarget | null} control
   */
  #clearFieldError(control) {
    if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return;
    if (control.getAttribute('aria-invalid') !== 'true') return;

    const message = this.querySelector(`[data-error-for="${control.name}"]`);
    control.setAttribute('aria-invalid', 'false');
    control.removeAttribute('aria-describedby');
    if (message instanceof HTMLElement) message.hidden = true;
  }

  #showSuccess() {
    const successUrl = this.dataset.successUrl;
    if (successUrl) {
      window.location.assign(window.NoomaWeglotChrome?.prefixFetchUrl?.(successUrl) ?? successUrl);
      return;
    }

    const form = this.#form;
    const success = this.#ref('success');
    if (form) form.hidden = true;
    if (success) success.hidden = false;
    this.#ref('success-title')?.focus();
  }

  /**
   * @param {SubmitEvent} event
   */
  async #onSubmit(event) {
    event.preventDefault();
    if (this.#sending) return;

    const form = this.#form;
    if (!form) return;

    const sendError = this.#ref('send-error');
    const sending = this.#ref('sending');
    if (sendError) sendError.hidden = true;

    const invalid = this.#invalidFields(form);
    if (invalid.length > 0) {
      this.#showFieldErrors(form, invalid);
      return;
    }

    const submit = form.querySelector('[type="submit"]');

    this.#sending = true;
    if (submit instanceof HTMLButtonElement) submit.disabled = true;
    if (sending) sending.hidden = false;

    try {
      const body = new URLSearchParams();
      for (const [name, value] of new FormData(form)) {
        if (typeof value === 'string') body.append(name, value);
      }

      const response = await fetch(form.action, { method: 'POST', body });
      if (response.ok) {
        this.#showSuccess();
        return;
      }

      const payload = await response.json().catch(() => null);
      if (response.status === 400 && Array.isArray(payload?.fields) && payload.fields.length > 0) {
        this.#showFieldErrors(form, payload.fields);
        return;
      }

      if (sendError) sendError.hidden = false;
    } catch {
      if (sendError) sendError.hidden = false;
    } finally {
      this.#sending = false;
      if (sending) sending.hidden = true;
      if (submit instanceof HTMLButtonElement && !form.hidden) submit.disabled = false;
    }
  }
}

if (!customElements.get('dev-pro-signup')) {
  customElements.define('dev-pro-signup', DevProSignup);
}
